import React, { useState, useEffect, useCallback } from 'react';
import {
  Typography,
  Box,
  Fade,
  Paper,
  Grid,
  TextField,
  FormControlLabel,
  Switch,
  Button,
  CircularProgress,
  Alert,
  IconButton,
  InputAdornment,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Container,
} from '@mui/material';
import { Save, Send, Visibility, VisibilityOff } from '@mui/icons-material';
import AppLayout from '../components/layout/AppLayout';
import { THEME_COLORS, OPERATION_STATUS, UX_TIMING } from '../constants';
import { useSettings } from '../hooks/useSettings';
import { useAuth } from '../contexts/AuthContext';
import ConfirmationModal from '../components/forms/ConfirmationModal';

const SettingsPageComponent = () => {
  const { settings, setSettings, loading, initialLoad, error, saveSettings, sendTestMessage } = useSettings();
  
  const canEditSettings = true;

  const [hasChanges, setHasChanges] = useState(false);
  const [showToken, setShowToken] = useState(false);
  
  // Estados para el modal de "Enviar Prueba"
  const [showTestModal, setShowTestModal] = useState(false);
  const [testStatus, setTestStatus] = useState(null);
  const [testMessage, setTestMessage] = useState({ title: '', subtitle: '' });

  // Estados para el modal de "Guardar Cambios"
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [saveStatus, setSaveStatus] = useState(null);
  const [saveMessage, setSaveMessage] = useState({ title: '', subtitle: '' });

  const [testPhone, setTestPhone] = useState('');
  const [testMessageBody, setTestMessageBody] = useState('Este es un mensaje de prueba desde el dashboard.');
  const [testQueueType, setTestQueueType] = useState('dispositivos');

  useEffect(() => {
    if (!loading) {
      setHasChanges(false);
    }
  }, [loading]);

  const handleSettingChange = useCallback((key, value) => {
    setSettings(prev => ({ ...prev, [key]: value }));
    if (!hasChanges) {
      setHasChanges(true);
    }
  }, [hasChanges, setSettings]);

  const handleSave = async () => {
    setShowSaveModal(true);
    setSaveStatus(OPERATION_STATUS.LOADING);
    setSaveMessage({
      title: 'Guardando Cambios',
      subtitle: 'Por favor espera mientras se actualiza la configuración...',
    });

    const result = await saveSettings(settings);

    if (result.success) {
      setSaveStatus(OPERATION_STATUS.SUCCESS);
      setSaveMessage({
        title: '¡Configuración Guardada!',
        subtitle: result.message || 'Los cambios se han guardado exitosamente.',
      });
      setHasChanges(false);
    } else {
      setSaveStatus(OPERATION_STATUS.ERROR);
      setSaveMessage({
        title: 'Error al Guardar',
        subtitle: result.message || 'No se pudo guardar la configuración.',
      });
    }

    const closeAfter = result.success ? UX_TIMING.SUCCESS_DISPLAY_TIME : UX_TIMING.ERROR_DISPLAY_TIME;
    setTimeout(() => {
      setShowSaveModal(false);
      setTimeout(() => setSaveStatus(null), 300);
    }, closeAfter);
  };

  const handleSendTest = async () => {
    if (!testPhone || !testMessageBody) return;

    setShowTestModal(true);
    setTestStatus(OPERATION_STATUS.LOADING);
    setTestMessage({
      title: 'Enviando Mensaje',
      subtitle: 'Por favor espera mientras se procesa la solicitud...',
    });

    const result = await sendTestMessage(testPhone, testMessageBody, testQueueType);

    if (result.success) {
      setTestStatus(OPERATION_STATUS.SUCCESS);
      setTestMessage({
        title: '¡Solicitud Enviada!',
        subtitle: result.message || `El mensaje de prueba a ${testPhone} ha sido enviado a la cola de ${testQueueType}.`,
      });
    } else {
      setTestStatus(OPERATION_STATUS.ERROR);
      setTestMessage({
        title: 'Error al Enviar',
        subtitle: result.message || 'No se pudo procesar la solicitud de envío.',
      });
    }

    const closeAfter = result.success ? UX_TIMING.SUCCESS_DISPLAY_TIME : UX_TIMING.ERROR_DISPLAY_TIME;
    setTimeout(() => {
      setShowTestModal(false);
      setTimeout(() => setTestStatus(null), 300);
    }, closeAfter);
  };

  const renderContent = () => {
    if (initialLoad) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      );
    }

    if (error) {
      return (
        <Alert severity="error" sx={{ m: 4 }}>
          {error}
        </Alert>
      );
    }

    return (
      <Grid container spacing={4}>
        {/* Columna de configuraciones de WhatsApp */}
        <Grid item xs={12} md={7}>
          <Paper sx={{ p: 3, border: `1px solid ${THEME_COLORS.BORDER}`, borderRadius: '12px', height: '100%', display: 'flex', flexDirection: 'column' }}>
            <Typography variant="h6" sx={{ color: THEME_COLORS.TEXT_PRIMARY, fontWeight: 600, mb: 3 }}>
              Configuración de WhatsApp
            </Typography>
            <Grid container spacing={3} sx={{ flexGrow: 1 }}>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.whatsapp_enabled}
                      onChange={(e) => handleSettingChange('whatsapp_enabled', e.target.checked)}
                    />
                  }
                  label="Habilitar servicio de WhatsApp"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="URL de la API de WhaConnect"
                  value={settings.whatsapp_api_url}
                  onChange={(e) => handleSettingChange('whatsapp_api_url', e.target.value)}
                  placeholder="https://api.proveedor.com/messages/send"
                  disabled={!settings.whatsapp_enabled}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Token de la API"
                  type={showToken ? 'text' : 'password'}
                  value={settings.whatsapp_api_token}
                  onChange={(e) => handleSettingChange('whatsapp_api_token', e.target.value)}
                  placeholder="••••••••••••••••••••"
                  disabled={!settings.whatsapp_enabled}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton onClick={() => setShowToken(!showToken)} edge="end">
                          {showToken ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="ID de la Cola de Suscripción"
                  value={settings.whatsapp_subscription_queue_id}
                  onChange={(e) => handleSettingChange('whatsapp_subscription_queue_id', e.target.value)}
                  placeholder="Ej: 12345"
                  disabled={!settings.whatsapp_enabled}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="ID de la Cola de Dispositivos"
                  value={settings.whatsapp_dispositivos_queue_id}
                  onChange={(e) => handleSettingChange('whatsapp_dispositivos_queue_id', e.target.value)}
                  placeholder="Ej: 67890"
                  disabled={!settings.whatsapp_enabled}
                />
              </Grid>
              <Grid item xs={12}><Divider /></Grid>
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.whatsapp_default_send_signature}
                      onChange={(e) => handleSettingChange('whatsapp_default_send_signature', e.target.checked)}
                      disabled={!settings.whatsapp_enabled}
                    />
                  }
                  label="Enviar firma por defecto"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.whatsapp_default_close_ticket}
                      onChange={(e) => handleSettingChange('whatsapp_default_close_ticket', e.target.checked)}
                      disabled={!settings.whatsapp_enabled}
                    />
                  }
                  label="Cerrar ticket por defecto"
                />
              </Grid>
            </Grid>
            <Box sx={{ mt: 4, pt: 3, borderTop: `1px solid ${THEME_COLORS.BORDER}` }}>
              <Button
                fullWidth
                variant="contained"
                onClick={handleSave}
                startIcon={saveStatus === OPERATION_STATUS.LOADING ? <CircularProgress size={20} color="inherit" /> : <Save />}
                disabled={!hasChanges || saveStatus === OPERATION_STATUS.LOADING}
                sx={{
                  py: 1.5,
                  borderRadius: '12px',
                  fontWeight: 600,
                  backgroundColor: THEME_COLORS.PRIMARY,
                  color: THEME_COLORS.WHITE,
                  '&:hover': {
                    backgroundColor: THEME_COLORS.ACCENT
                  }
                }}
              >
                Guardar Cambios
              </Button>
            </Box>
          </Paper>
        </Grid>

        {/* Columna para enviar mensaje de prueba */}
        <Grid item xs={12} md={5}>
          <Paper sx={{ p: 3, border: `1px solid ${THEME_COLORS.BORDER}`, borderRadius: '12px', height: '100%', display: 'flex', flexDirection: 'column' }}>
            <Typography variant="h6" sx={{ color: THEME_COLORS.TEXT_PRIMARY, fontWeight: 600, mb: 3 }}>
              Enviar Mensaje de Prueba
            </Typography>
            <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', gap: 3 }}>
              <TextField
                fullWidth
                label="Número de Teléfono"
                value={testPhone}
                onChange={(e) => setTestPhone(e.target.value)}
                placeholder="Ej: 521XXXXXXXXXX"
                helperText="Incluye código de país y área."
                disabled={!settings.whatsapp_enabled}
              />
              <FormControl fullWidth disabled={!settings.whatsapp_enabled}>
                <InputLabel>Tipo de Cola</InputLabel>
                <Select
                  value={testQueueType}
                  label="Tipo de Cola"
                  onChange={(e) => setTestQueueType(e.target.value)}
                >
                  <MenuItem value="suscripciones">Suscripciones</MenuItem>
                  <MenuItem value="dispositivos">Dispositivos</MenuItem>
                </Select>
              </FormControl>
              <TextField
                fullWidth
                multiline
                rows={4}
                label="Mensaje"
                value={testMessageBody}
                onChange={(e) => setTestMessageBody(e.target.value)}
                disabled={!settings.whatsapp_enabled}
              />
            </Box>
            <Box sx={{ mt: 4, pt: 3, borderTop: `1px solid ${THEME_COLORS.BORDER}` }}>
              <Button
                fullWidth
                variant="outlined"
                onClick={handleSendTest}
                startIcon={testStatus === OPERATION_STATUS.LOADING ? <CircularProgress size={20} /> : <Send />}
                disabled={!settings.whatsapp_enabled || testStatus === OPERATION_STATUS.LOADING || !testPhone}
                sx={{
                  py: 1.5,
                  borderRadius: '12px',
                  fontWeight: 600
                }}
              >
                Enviar Prueba
              </Button>
            </Box>
          </Paper>
        </Grid>
      </Grid>
    );
  };

  return (
    <AppLayout currentPage="settings">
      <Container maxWidth="xl">
        <Fade in timeout={500}>
          <Box>
            <Box sx={{ mb: 4 }}>
              <Typography variant="h4" sx={{ color: THEME_COLORS.TEXT_PRIMARY, fontWeight: 700 }}>
                Configuración del Sistema
              </Typography>
              <Typography sx={{ color: THEME_COLORS.TEXT_MUTED, mt: 1 }}>
                Ajusta los parámetros de integración y otras configuraciones generales.
              </Typography>
            </Box>

            {renderContent()}

          </Box>
        </Fade>
      </Container>
      
      {/* Modal para guardar cambios */}
      {showSaveModal && (
        <ConfirmationModal
          open={showSaveModal}
          status={saveStatus}
          title={saveMessage.title}
          subtitle={saveMessage.subtitle}
          hideActions={true}
        />
      )}

      {/* Modal para enviar prueba */}
      {showTestModal && (
        <ConfirmationModal
          open={showTestModal}
          status={testStatus}
          title={testMessage.title}
          subtitle={testMessage.subtitle}
          hideActions={true}
        />
      )}

    </AppLayout>
  );
};

const SettingsPage = React.memo(SettingsPageComponent);
SettingsPage.displayName = 'SettingsPage';

export default SettingsPage; 